import { Fragment, useEffect, useMemo, useState } from "react";
import { getProductOpportunities } from "../../distflowAPI/salesAnalyticsApi";
import "./ProductOpportunities.css";

const PAGE = 25;
const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const exactCurrency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const money = (value) => currency.format(Number(value || 0));
const pct = (value, digits = 0) => value == null ? "—" : `${Number(value).toFixed(digits)}%`;
const num = (value) => Number(value || 0);

const VIEWS = [
  { key: "opportunity", label: "Biggest opportunity", hint: "Products already selling well to some customers of a type, but missing from many similar customers. Ranked by potential gross profit per week.",
    sort: null },
  { key: "rising", label: "Rising", hint: "Gaining customers compared with the comparison period.", needsComparison: true,
    sort: (a, b) => (b.customers_gained - b.customers_lost) - (a.customers_gained - a.customers_lost) || num(b.sales_change_amount) - num(a.sales_change_amount) },
  { key: "falling", label: "Falling", hint: "Losing customers, or sales down 25% or more, compared with the comparison period.", needsComparison: true,
    sort: (a, b) => (b.customers_lost - b.customers_gained) - (a.customers_lost - a.customers_gained) || num(a.sales_change_amount) - num(b.sales_change_amount) },
  { key: "not_reordered", label: "Not reordered", hint: "Five or more customers bought it, but fewer than 30% bought it again on another day. Worth asking why before pushing it.",
    sort: (a, b) => num(a.reorder_rate_percent) - num(b.reorder_rate_percent) },
  { key: "low_margin", label: "Low margin", hint: "Gross margin under 10%. Selling more of these adds little profit.",
    sort: (a, b) => num(a.margin_percent) - num(b.margin_percent) },
  { key: "all", label: "All products", hint: "Every product sold in the selected dates, by sales.",
    sort: (a, b) => num(b.sales) - num(a.sales) },
];

function Momentum({ row }) {
  if (row.customers_gained === undefined) return <span className="product-opps-muted">—</span>;
  const net = row.customers_gained - row.customers_lost;
  const tone = net > 0 ? "gain" : net < 0 ? "decline" : "flat";
  return <span className={`product-opps-momentum product-opps-momentum--${tone}`}>
    {net > 0 ? "+" : net < 0 ? "−" : "±"}{Math.abs(net)} customers
    <small>+{row.customers_gained} new · −{row.customers_lost} lost{row.sales_change_percent != null && ` · sales ${num(row.sales_change_percent) > 0 ? "+" : ""}${pct(row.sales_change_percent)}`}</small>
  </span>;
}

function ReachBar({ item }) {
  const width = Math.min(100, num(item.reach_percent));
  return <li className={item.counted ? "" : "is-muted"}>
    <span className="product-opps-reach-name">{item.account_type}</span>
    <span className="product-opps-reach-track" aria-hidden="true"><i style={{ width: `${width}%` }} /></span>
    <span className="product-opps-reach-value">{item.buyers} of {item.customers} ({pct(item.reach_percent)})</span>
    <span className="product-opps-reach-potential">{item.counted ? `${item.non_buyers} not buying · up to ${money(item.potential_weekly)}/wk` : "Too few buyers to estimate"}</span>
  </li>;
}

export default function ProductOpportunities({ request, filters = [], onSelectProduct, onSelectCustomer }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState("opportunity");
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [visible, setVisible] = useState(PAGE);
  const signature = JSON.stringify({ ...request, filters });

  useEffect(() => {
    if (!request?.start_date) return undefined;
    const controller = new AbortController();
    const params = JSON.parse(signature);
    setLoading(true);
    setError("");
    Promise.resolve()
      .then(() => getProductOpportunities(params, { signal: controller.signal }))
      .then((result) => { if (!controller.signal.aborted) { setData(result || null); setExpanded(null); setVisible(PAGE); } })
      .catch((requestError) => {
        if (controller.signal.aborted || requestError?.code === "ERR_CANCELED") return;
        setData(null);
        setError("Product opportunities could not be loaded.");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const hasComparison = Boolean(data?.comparison);
  const activeView = VIEWS.find((item) => item.key === view && (!item.needsComparison || hasComparison)) || VIEWS[0];
  const rows = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    const list = (data?.rows || []).filter((row) =>
      (activeView.key === "all" || row.tags?.includes(activeView.key)) &&
      (category === "all" || String(row.category_id) === category) &&
      (!term || `${row.name} ${row.sku || ""} ${row.vendor || ""}`.toLocaleLowerCase().includes(term))
    );
    return activeView.sort ? [...list].sort(activeView.sort) : list;
  }, [data, activeView, category, query]);

  if (!data && !loading && !error) return null;
  const summary = data?.summary;
  const tagCount = (key) => key === "all" ? data?.total : summary?.tags?.[key] || 0;

  return <section className="product-opps" aria-label="Products to push" aria-busy={loading}>
    <div className="sales-analytics-section-head">
      <div><span className="sales-analytics-eyebrow">What to sell more of</span><h2>Products to push</h2></div>
      {summary && <span className="sales-analytics-section-aside">Gross profit {money(summary.gross_profit)} · margin {pct(summary.margin_percent, 1)} · cost recorded for {pct(summary.cost_coverage_percent)} of item sales</span>}
    </div>
    {error && <p className="product-opps-error" role="alert">{error}</p>}
    {loading && !data && <p className="product-opps-empty">Scoring products…</p>}
    {data && <div className={`product-opps-card${loading ? " product-opps-card--refreshing" : ""}`}>
      <div className="product-opps-views" role="tablist" aria-label="Product views">
        {VIEWS.map((item) => {
          const disabled = item.needsComparison && !hasComparison;
          return <button type="button" role="tab" key={item.key} aria-selected={activeView.key === item.key} disabled={disabled}
            title={disabled ? "Choose a comparison to see this" : item.hint}
            className={activeView.key === item.key ? "is-selected" : ""} onClick={() => { setView(item.key); setVisible(PAGE); setExpanded(null); }}>
            {item.label} {!disabled && <b>{tagCount(item.key)}</b>}
          </button>;
        })}
      </div>
      <p className="product-opps-hint">{activeView.hint}</p>
      <div className="product-opps-tools">
        <input type="search" aria-label="Search products, SKUs or vendors" placeholder="Search products, SKUs or vendors…" value={query} onChange={(event) => { setQuery(event.target.value); setVisible(PAGE); }} />
        <label><span>Category</span>
          <select value={category} onChange={(event) => { setCategory(event.target.value); setVisible(PAGE); }}>
            <option value="all">All categories</option>
            {(data.categories || []).map((item) => <option key={String(item.id)} value={String(item.id)}>{item.name} · {pct(item.margin_percent)} margin</option>)}
          </select>
        </label>
      </div>
      {rows.length ? <div className="product-opps-table-wrap"><table aria-label="Products to push">
        <thead><tr>
          <th scope="col">Product</th><th scope="col">Sales / week</th><th scope="col">Margin</th>
          <th scope="col">Customers</th><th scope="col">Reorder rate</th><th scope="col">Momentum</th>
          <th scope="col">Opportunity</th><th scope="col"><span className="product-opps-hidden">Details</span></th>
        </tr></thead>
        <tbody>{rows.slice(0, visible).map((row) => {
          const open = expanded === row.key;
          return <Fragment key={row.key}>
            <tr className={open ? "is-open" : ""}>
              <th scope="row">
                <button type="button" className="product-opps-name" title={`Focus the page on ${row.name}`} onClick={() => onSelectProduct?.(row)}>{row.name}</button>
                <small>{[row.sku, row.category, row.vendor].filter(Boolean).join(" · ")}</small>
              </th>
              <td>{money(row.weekly_sales)}<small>{money(row.sales)} total</small></td>
              <td className={row.tags?.includes("low_margin") ? "product-opps-low" : ""}>{pct(row.margin_percent, 1)}
                <small>{row.gross_profit != null ? `${money(row.gross_profit)} profit` : "no cost recorded"}{row.cost_coverage_percent != null && num(row.cost_coverage_percent) < 100 && num(row.cost_coverage_percent) > 0 && ` · ${pct(row.cost_coverage_percent)} costed`}</small></td>
              <td>{row.customers}<small>{pct(row.reach_percent)} of active</small></td>
              <td className={row.tags?.includes("not_reordered") ? "product-opps-low" : ""}>{row.customers >= 3 ? pct(row.reorder_rate_percent) : "—"}<small>{row.repeat_customers} bought again</small></td>
              <td><Momentum row={row} /></td>
              <td className="product-opps-opportunity">{num(row.potential_weekly) > 0 ? <>Up to {money(row.potential_weekly)}/wk<small>{row.potential_profit_weekly != null ? `${money(row.potential_profit_weekly)}/wk profit` : "margin unknown"} · {row.target_total} customers</small></> : <span className="product-opps-muted">—</span>}</td>
              <td><button type="button" className="product-opps-expand" aria-expanded={open} aria-label={`${open ? "Hide" : "Show"} details for ${row.name}`} onClick={() => setExpanded(open ? null : row.key)}>{open ? "−" : "+"}</button></td>
            </tr>
            {open && <tr className="product-opps-details"><td colSpan={8}>
              <div className="product-opps-details-grid">
                <div><strong>Reach by account type</strong>
                  {row.reach_by_account_type?.length ? <ul className="product-opps-reach">{row.reach_by_account_type.map((item) => <ReachBar key={String(item.account_type_id)} item={item} />)}</ul> : <p>No buyers in the selected dates.</p>}
                </div>
                <div><strong>Who to offer it to</strong>
                  {row.targets?.length ? <ul className="product-opps-targets">{row.targets.map((target) => <li key={target.key}>
                    <button type="button" className="product-opps-name" onClick={() => onSelectCustomer?.(target)}>{target.name}</button>
                    <span>{target.account_type} · spends {exactCurrency.format(num(target.weekly_spend))}/wk with you</span>
                  </li>)}</ul> : <p>{num(row.potential_weekly) > 0 ? "Open this product from the top of the list to see names." : "No account type has enough buyers yet to suggest similar customers."}</p>}
                  {row.target_total > (row.targets?.length || 0) && row.targets?.length > 0 && <p>+{row.target_total - row.targets.length} more similar customers not buying it.</p>}
                </div>
              </div>
            </td></tr>}
          </Fragment>;
        })}</tbody>
      </table></div> : <p className="product-opps-empty">{data.rows?.length ? "No products in this view." : "No product sales in the selected dates."}</p>}
      {rows.length > visible && <button type="button" className="product-opps-more" onClick={() => setVisible(visible + PAGE)}>Show {Math.min(PAGE, rows.length - visible)} more of {rows.length - visible}</button>}
      {data.total > (data.rows?.length || 0) && <p className="product-opps-note">Showing the top {data.rows.length} of {data.total} products.</p>}
    </div>}
  </section>;
}
